import { Box, Button, Card, Stack, Tab, Tabs, Typography, useMediaQuery } from "@mui/material"
import { BoxTabs, CardDescription, ImageItem, ImageTab, shortDescriptionClampText, TabsStyle, TypTitle } from "./style"
import { useOutletContext } from "react-router-dom";
import { PureLightTheme } from "../../../../styles/theme";
import { MobileInfo } from "./MobileInfo";
import { StepperDescription } from "./StepperDescription";
import { IconImagesLeft } from "../../../../icons/IconImagesLeft";
import { IconImagesRight } from "../../../../icons/IconImagesRight";
import { ImageGalleryModal } from "../../../ImageGalleryModal"
import { useState, useRef, useEffect, useMemo } from "react";
import modalEmitter from "../../../ModalComponent/modalEmitter"
import { ShortDescriptionModalBody } from "../../../Modals/ShortDescriptionModalBody"
import { IProjectResponse } from "../../../../api/types";

export const Description = () => {
    const isTablet = useMediaQuery(PureLightTheme.breakpoints.down("md"));
    const project = useOutletContext<IProjectResponse>();

    const [galleryOpen, setGalleryOpen] = useState(false);
    const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
    const [isShortDescriptionOverflow, setIsShortDescriptionOverflow] = useState(false);
    const shortDescriptionRef = useRef<HTMLSpanElement>(null);

    const shortDescription = project.about.description ?? ''
    const fullDescription = useMemo(
        () => (project.about.full_description ?? '').trim() || shortDescription,
        [project.about.full_description, shortDescription],
    )
    const hasDistinctFullDescription = Boolean(
        (project.about.full_description ?? '').trim()
        && (project.about.full_description ?? '').trim() !== shortDescription.trim(),
    )
    const canExpandDescription = isShortDescriptionOverflow || hasDistinctFullDescription

    const handleModalOpen = () => {
        if (!canExpandDescription) return

        modalEmitter.emitOpenModal({
            dialog: {
                title: hasDistinctFullDescription ? "Полное описание" : "Краткое описание",
                body: (
                    <ShortDescriptionModalBody
                        text={fullDescription}
                    />
                ),
            },
            closeAtion: { visible: false },
            confirmAtion: { visible: false },
        })
    };

    const openGallery = (index: number) => {
        setGalleryInitialIndex(index);
        setGalleryOpen(true);
    };

    useEffect(() => {
        const element = shortDescriptionRef.current;
        if (!element) return;
    
        const observer = new ResizeObserver(() => {
            setIsShortDescriptionOverflow(element.scrollHeight > element.clientHeight);
        });
    
        observer.observe(element);
    
        return () => observer.disconnect(); 
    }, [shortDescription]); 
    
    return (
        <Box>
            {isTablet && (
                <Box sx={{mb: 2}}>
                    <MobileInfo project={project} />
                </Box>
            )}
            {project.image_gallery && project.image_gallery.length > 0 && (
                <Box sx={BoxTabs}>
                    <Tabs
                        value={false}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={TabsStyle}
                        slots={{
                            StartScrollButtonIcon: IconImagesLeft,
                            EndScrollButtonIcon: IconImagesRight,
                        }}
                    >
                        {project.image_gallery?.map((src, index) => (
                            <Tab 
                                key={index}
                                disableRipple
                                sx={ImageTab}
                                onClick={() => openGallery(index)}
                                icon={
                                    <Box 
                                        key={index} 
                                        component="img" 
                                        src={src} 
                                        alt={"Image"} 
                                        sx={ImageItem} 
                                    />
                                }
                            />
                        ))}
                    </Tabs>
                </Box>
            )}
            <StepperDescription indexNumber={project.stage} />
            <Stack direction="column" gap={2}>
                <Card variant="primaryAdaptiveCard" sx={CardDescription}>
                    <Typography variant="h3" sx={TypTitle}>Краткое описание</Typography>
                    <Typography
                        ref={shortDescriptionRef}
                        variant="body1"
                        sx={shortDescriptionClampText}
                    >
                        {shortDescription}
                    </Typography>
                    {canExpandDescription && (
                        <Button variant="action" onClick={handleModalOpen} sx={{ mt: 1, alignSelf: 'flex-start' }}>
                            Полное описание
                        </Button>
                    )}
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardDescription}>
                    <Typography variant="h3" sx={TypTitle}>Проблема/возможность (фактор среды)</Typography>
                    <Typography variant="body1">{project.about.env_factor ?? ''}</Typography>
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardDescription}>
                    <Typography variant="h3" sx={TypTitle}>Что должно получиться?</Typography>
                    <Typography variant="body1">{project.about.expected_result ?? ''}</Typography>
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardDescription}>
                    <Typography variant="h3" sx={TypTitle}>Исходное положение дел</Typography>
                    <Typography variant="body1">{project.about.initial_state ?? ''}</Typography>
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardDescription}>
                    <Typography variant="h3" sx={TypTitle}>Что необходимо сделать по проекту?</Typography>
                    <Typography variant="body1">{project.about.necessary_actions ?? ''}</Typography>
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardDescription}>
                    <Typography variant="h3" sx={TypTitle}>Как будем реализовывать?</Typography>
                    <Typography variant="body1">{project.about.implementation ?? ''}</Typography>
                </Card>
            </Stack>
            {/* TODO: проверить галерею изображений проекта когда появится */}
            <ImageGalleryModal
                open={galleryOpen}
                onClose={() => setGalleryOpen(false)}
                images={project.image_gallery ?? null}
                initialIndex={galleryInitialIndex}
            />
        </Box>
    )
}
